/**
 * Make a context step appear: run its page's `reveal` recipe — first on the
 * current screen, then (when the recipe has `offer` / `go` and the caller can
 * navigate) on a suitable other offer or route.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { bringOffer, startNewOffer } from "./ask/journey";
import { revealStep } from "./dom";
import { helpText } from "./strings";
import type { HelpLanguage, HelpReveal, HelpStep, PageHelp } from "./types";

/** A screen reached by navigating may still be loading its data. */
export const PAGE_LOAD_MS = 8000;

export interface RevealNavigation {
  navigate: (to: string) => void;
  /** Asked before leaving a new, unsaved offer; false keeps the reader where they are. */
  confirmLeave: () => boolean;
}

export function useRevealNavigation(language: HelpLanguage): RevealNavigation {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      navigate,
      confirmLeave: () =>
        window.confirm(
          helpText(language)(
            "help.reveal.leaveNewOffer",
            "This is shown on another offer. Leave this new offer? What you entered here is not saved."
          )
        ),
    }),
    [navigate, language]
  );
}

export function revealRecipe(page: PageHelp, step: HelpStep): HelpReveal | undefined {
  return step.context ? page.reveal?.[step.context] : undefined;
}

function isNewOffer(): boolean {
  return window.location.pathname === "/offer";
}

export async function revealHelpStep(
  page: PageHelp,
  step: HelpStep,
  navigation?: RevealNavigation,
  timeoutMs?: number
): Promise<boolean> {
  const recipe = revealRecipe(page, step);
  if (!recipe) return false;
  const press = Array.isArray(recipe) ? recipe : recipe.press;
  if (await revealStep(step, press, timeoutMs)) return true;
  if (Array.isArray(recipe) || !navigation || (!recipe.offer && !recipe.newOffer)) return false;
  // already where the recipe would go: the step is simply not there (yet)
  if (recipe.newOffer && isNewOffer() && new URLSearchParams(window.location.search).get("type") === recipe.newOffer) {
    return false;
  }
  if (isNewOffer() && !navigation.confirmLeave()) return false;
  const arrived = recipe.offer
    ? await bringOffer(recipe.offer, navigation.navigate)
    : await startNewOffer(recipe.newOffer!, navigation.navigate);
  if (!arrived) return false;
  return revealStep(step, press, PAGE_LOAD_MS);
}
